import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { BUSINESS, BANK_DETAILS } from '../config/constants';

const GREEN = [27, 79, 42]; // #1B4F2A
const GOLD = [200, 168, 75]; // #C8A84B

function invoiceReference(invoice) {
  return invoice.reference || `LDH-${invoice.id.slice(-6).toUpperCase()}`;
}

async function loadLogoDataUrl() {
  const res = await fetch('/logo512.png');
  if (!res.ok) throw new Error('logo fetch failed');
  const blob = await res.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export async function generateInvoicePdf(invoice) {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 40;
  const reference = invoiceReference(invoice);

  let logoDataUrl = null;
  try {
    logoDataUrl = await loadLogoDataUrl();
  } catch {
    // Logo is a nice-to-have — carry on without it if it can't be loaded.
  }

  // Header band
  doc.setFillColor(...GREEN);
  doc.rect(0, 0, pageWidth, 90, 'F');
  const textStartX = logoDataUrl ? margin + 72 : margin;
  if (logoDataUrl) doc.addImage(logoDataUrl, 'PNG', margin, 15, 60, 60);
  doc.setTextColor(255, 255, 255);
  doc.setFont('times', 'bold');
  doc.setFontSize(20);
  doc.text(BUSINESS.name, textStartX, 45);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text(BUSINESS.tagline, textStartX, 62);

  // Business contact block, right-aligned. The trading address is long
  // enough to wrap onto two lines, so measure actual wrapped height rather
  // than assuming one line per entry — otherwise later fixed-position
  // elements (reference/date/status) end up printed on top of it.
  doc.setTextColor(60, 60, 60);
  doc.setFontSize(9);
  const contactLineHeight = 11;
  const contactWidth = 220;
  let contactY = 112;
  [BUSINESS.address, BUSINESS.phone, BUSINESS.email, BUSINESS.website].forEach(line => {
    const wrapped = doc.splitTextToSize(line, contactWidth);
    wrapped.forEach(w => {
      doc.text(w, pageWidth - margin, contactY, { align: 'right' });
      contactY += contactLineHeight;
    });
  });

  // Title + reference/date/status — start below whichever is taller: the
  // fixed header band, or the (variable-height) contact block.
  let y = Math.max(140, contactY + 16);

  doc.setTextColor(...GREEN);
  doc.setFont('times', 'bold');
  doc.setFontSize(18);
  doc.text('INVOICE', margin, y);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...(invoice.paid ? GREEN : [180, 60, 20]));
  doc.text(invoice.paid ? 'PAID' : 'OUTSTANDING', pageWidth - margin, y, { align: 'right' });

  y += 22;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(40, 40, 40);
  const dateStr = invoice.createdAt?.toDate?.()
    ? invoice.createdAt.toDate().toLocaleDateString('en-GB')
    : new Date().toLocaleDateString('en-GB');
  doc.text(`Reference: ${reference}`, margin, y);
  y += 14;
  doc.text(`Date: ${dateStr}`, margin, y);

  y += 28;

  // Bill to
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(40, 40, 40);
  doc.text('Bill to', margin, y);
  y += 14;
  doc.setFont('helvetica', 'normal');
  doc.text(invoice.clientName || invoice.clientEmail || '—', margin, y);
  y += 13;
  if (invoice.clientEmail) {
    doc.text(invoice.clientEmail, margin, y);
    y += 13;
  }

  if (invoice.description) {
    y += 6;
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(9);
    const descLines = doc.splitTextToSize(invoice.description, pageWidth - margin * 2);
    doc.text(descLines, margin, y);
    y += descLines.length * 11;
  }

  y += 16;

  // Line-item table
  const rows = invoice.breakdown?.length > 0
    ? invoice.breakdown.map(item => [item.label, `£${item.amount.toFixed(2)}`])
    : [[invoice.description || 'Services rendered', `£${(invoice.amount || 0).toFixed(2)}`]];

  autoTable(doc, {
    startY: y,
    head: [['Description', 'Amount']],
    body: rows,
    theme: 'grid',
    headStyles: { fillColor: GREEN, textColor: 255, fontStyle: 'bold' },
    columnStyles: { 1: { halign: 'right', cellWidth: 100 } },
    margin: { left: margin, right: margin },
    styles: { fontSize: 10, cellPadding: 6 },
  });

  y = doc.lastAutoTable.finalY + 24;

  // Total
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(40, 40, 40);
  doc.text('Total due', pageWidth - margin - 140, y);
  doc.setTextColor(...GOLD);
  doc.text(`£${(invoice.amount || 0).toFixed(2)}`, pageWidth - margin, y, { align: 'right' });

  y += 26;

  // Payment details block
  const blockHeight = 118;
  doc.setDrawColor(210, 210, 200);
  doc.setFillColor(248, 247, 240);
  doc.roundedRect(margin, y, pageWidth - margin * 2, blockHeight, 6, 6, 'FD');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...GREEN);
  doc.text('Payment details', margin + 14, y + 20);

  const payLines = [
    ['Bank', BANK_DETAILS.bankName],
    ['Account name', BANK_DETAILS.accountName],
    ['Sort code', BANK_DETAILS.sortCode],
    ['Account number', BANK_DETAILS.accountNumber],
    ['Payment reference', reference],
  ];
  let py = y + 40;
  payLines.forEach(([label, val]) => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.5);
    doc.setTextColor(40, 40, 40);
    doc.text(`${label}:`, margin + 14, py);
    doc.setFont('helvetica', 'normal');
    doc.text(String(val), margin + 130, py);
    py += 14;
  });

  if (invoice.paid) {
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(8.5);
    doc.setTextColor(120, 120, 120);
    doc.text('This invoice has been paid in full — details above are for your records.', margin + 14, py + 6);
  }

  doc.save(`Invoice-${reference}.pdf`);
}
